import {Close, Edit} from '@mui/icons-material';
import {Box, Button, IconButton, Typography} from '@mui/material';
import {FC, useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {FormattedMessage, useIntl} from 'react-intl';
import {ocppApi} from 'src/api';
import {useAppSelector} from 'src/app/hooks';
import {SelectOcppTagModal} from 'src/components/chargers/SelectOcppTagModal';
import {ColoredStatus} from 'src/components/common/ColoredStatus';
import {ProgressActiveChargingIcon} from 'src/components/icons/ProgressActiveChargingIcon';
import {ChargePointStatus, ChargingStatusEnum, ConnectorStatusEnum} from 'src/enums';
import {usePersona} from 'src/permissions/hooks';
import {ActiveTransactionRequest, CHARGING_STATUS_TYPE} from 'src/types';
import {convertTime, formatPrice, isErrorObject} from 'src/utils/helper';
import {NotifyError} from 'src/utils/notification';
import {getUserDisplayName} from 'src/utils/userNameFormatter';
import {
    bodySx,
    connectorBadgeSx,
    connectorLabelSx,
    connectorPowerSx,
    connectorTypeSx,
    deliveredLabelSx,
    deliveredValueSx,
    dotSx,
    emptySx,
    figuresRowSx,
    hintSx,
    metaCostSx,
    metaNameSx,
    metaRowSx,
    metaTagSx,
    sectionButtonSx,
    sideFiguresSx,
    sideValueSx,
    specRowSx,
    tagFieldSx,
    tagLabelSx,
    tagPickSx,
    tagValueSx,
    unitSx,
} from './ActiveCharging.sx';
import {isSessionRunning} from './ActiveCharging.state';
import {ConnectorSessionType} from './ActiveCharging.types';

/**
 * The selected socket: what it is doing, who is on it, and the one thing you can do about it.
 *
 * Everything here is this connector's own - its figures, its card, its button, its rights - because
 * the card above used to share all four between every socket of the station, and shared them with
 * whichever connector the server happened to list first. Mounted with the connector's id as its key,
 * so switching tabs starts a clean panel rather than carrying one socket's chosen card to the next.
 */
export const ConnectorSession: FC<ConnectorSessionType> = ({
    charger,
    connector,
    status,
    description,
    showSpec,
    standalone,
    canManage,
    canReadActive,
    defaultTag,
}) => {
    const intl = useIntl();
    const persona = usePersona();
    // Selected one by one, and annotated: the slice is reached through a loosely typed root, and
    // taking the whole of `common` would re-render on news that is not a session's.
    const authInfo = useAppSelector((state) => state.common.authInfo);
    const CHARGING_STATUS: CHARGING_STATUS_TYPE[] = useAppSelector(
        (state) => state.common.CHARGING_STATUS
    );

    const [getActiveTransaction, activeTransaction] = ocppApi.useLazyGetActiveTransactionQuery();
    const [startCharging] = ocppApi.useStartChargingMutation();
    const [stopCharging] = ocppApi.useStopChargingMutation();

    // Seeded synchronously rather than by the effect below, because switching sockets remounts this
    // card (the band keys it on the connector). Starting at null meant every switch drew the field
    // empty for a frame - placeholder text, no clear button - and then filled it, so the row changed
    // its text and its width twice in one gesture. The card's query lives in the parent and is cached
    // by then, so on a switch the value is already here; on the very first mount it is not, and the
    // effect still applies it when it arrives.
    const [selectedTag, setSelectedTag] = useState<string | null>(defaultTag ?? null);
    const [isTagModalOpen, setIsTagModalOpen] = useState(false);

    const companyId = authInfo?.employee?.company?.id || authInfo?.company?.id;

    // Matched on the connector's database id, which is what the server puts in the message. The
    // number drawn in the badge is `connector.connectorId`, the socket's OCPP number - comparing
    // against that one never matches anything, and the panel would simply stop updating.
    const liveRow = useMemo(
        () => CHARGING_STATUS.find(
            (item) => item.chargerId === charger.id && item.connectorId === connector.id
        ),
        [CHARGING_STATUS, charger.id, connector.id]
    );

    const isCharging = status === ConnectorStatusEnum.CHARGING;
    const isPreparing = status === ConnectorStatusEnum.PREPARING;
    const sessionRunning = isSessionRunning(status);
    const isChargerOffline = charger.currentStatus === ChargePointStatus.OFFLINE;

    /**
     * Which session this socket is running, as the socket itself last said.
     *
     * A `CHARGING_STATUS` row is replaced and never removed, so it outlives the session that wrote
     * it. Read as "the latest thing this connector said", the figures of a session that ended hours
     * ago were shown as the current one's for the seconds between a car plugging in and its first
     * meter value - and, if the stop message was missed, for the whole of the next session. The
     * transaction id is in the message, so the row can be asked which session it is describing.
     */
    const liveTransactionId = liveRow && liveRow.status !== ChargingStatusEnum.STOPPED
        ? liveRow.transactionId
        : undefined;

    /**
     * Both accounts of the session, each held to that id, because both of them go stale.
     *
     * The row is the newer of the two, so it decides which transaction is the current one. The
     * answer from `/transactions/active` is dropped when it names another, which is what it holds
     * between a session starting and the refetch below coming back - without that, refusing the row
     * only moved the stale figures to the other source. With no row to measure against, which is a
     * page just reloaded and no meter value since, the server's answer is the only account there is.
     */
    const session = sessionRunning && liveTransactionId !== undefined ? liveRow : undefined;
    const reported = sessionRunning ? activeTransaction.data : undefined;
    const transaction = reported
        && (liveTransactionId === undefined || reported.id === liveTransactionId)
        ? reported
        : undefined;

    const wattsConsumed = session?.wattsConsumed ?? transaction?.wattsConsumed ?? 0;
    const amperage = session?.amperage ?? transaction?.amperage ?? '';
    const chargingTime = session?.chargingTimeMinutes ?? transaction?.chargingTimeMinutes ?? 0;
    // The message carries the price, recalculated on the meter value that produced it. Before this
    // the figure came only from `/transactions/active`, so it stood still between refetches.
    const cost = session?.cost ?? transaction?.cost;
    const sessionTag = session?.idTag?.idTag ?? transaction?.idTag?.idTag;
    const sessionUserId = session?.userId ?? transaction?.user?.id;
    const sessionUserName = getUserDisplayName(transaction?.user);

    useEffect(() => {
        if (!canReadActive) {
            return;
        }

        const input: ActiveTransactionRequest = {
            businessId: charger.business.id,
            chargerId: charger.id,
            connectorId: connector.id,
        };

        if (companyId) {
            input.companyId = companyId;
        }

        // A socket with nothing running is an ordinary answer here, not a failure worth a toast.
        void getActiveTransaction(input);
        // Asked again whenever the socket names a different session, or stops naming one. It used to
        // be asked once per mount: a session that started while the page was open never got the name
        // of the person it is billed to - the message carries a `userId` and no name - and a session
        // that ended left its answer in the cache for the next one to be measured against.
    }, [
        canReadActive,
        charger.business.id,
        charger.id,
        connector.id,
        companyId,
        liveTransactionId,
        sessionRunning,
        getActiveTransaction,
    ]);

    // Once only: after that the card is the reader's, and clearing it or picking another must stick.
    const defaultApplied = useRef(false);
    useEffect(() => {
        if (!defaultApplied.current && defaultTag) {
            defaultApplied.current = true;
            setSelectedTag((current) => current ?? defaultTag);
        }
    }, [defaultTag]);

    // An employee may start any socket but stop only the session the energy is billed to them. When
    // nothing says whose the session is, the question cannot be answered and is not asked.
    const isSessionOwner = sessionUserId === undefined || sessionUserId === authInfo?.id;
    const canControlSession = !persona.isEmployee || !isCharging || isSessionOwner;

    const startDisabled =
        isChargerOffline ||
        !canManage ||
        !(isCharging || isPreparing) ||
        !canControlSession ||
        (!isCharging && !selectedTag);

    const handleClick = useCallback(async () => {
        const asCompany = persona.isEmployee || persona.isCompany;

        try {
            if (isCharging) {
                await stopCharging({
                    chargerId: charger.id,
                    connectorId: connector.connectorId,
                    businessId: charger.business.id,
                    ...(asCompany && {companyId, rfId: sessionTag}),
                }).unwrap();
            } else if (selectedTag) {
                await startCharging({
                    rfId: selectedTag,
                    chargerId: charger.id,
                    connectorId: connector.connectorId,
                    businessId: charger.business.id,
                    ...(asCompany && {companyId}),
                }).unwrap();
            }
        } catch (error) {
            if (isErrorObject(error)) {
                NotifyError(error.data.message);
            }
        }
    }, [
        persona.isEmployee,
        persona.isCompany,
        isCharging,
        charger.id,
        charger.business.id,
        connector.connectorId,
        companyId,
        sessionTag,
        selectedTag,
        startCharging,
        stopCharging,
    ]);

    const handleSelectTag = useCallback((tagId: string) => setSelectedTag(tagId), []);
    const handleOpenTagModal = useCallback(() => setIsTagModalOpen(true), []);
    const handleCloseTagModal = useCallback(() => setIsTagModalOpen(false), []);
    const handleClearTag = useCallback(() => setSelectedTag(null), []);

    const showTagField = canManage && !sessionRunning;
    const showMeta = sessionRunning && Boolean(sessionUserName || sessionTag || cost);
    const hasSpec = showSpec && Boolean(description);

    return (
        <>
            <Box sx={bodySx}>
                {/* With one socket there is no tab band, so the name and the state belong here
                    instead. With several, the tab carries both and this line only says what the
                    socket is. */}
                {(hasSpec || standalone) && (
                    <Box sx={specRowSx}>
                        {standalone && (
                            <>
                                <Box sx={connectorBadgeSx(sessionRunning)}>{connector.connectorId}</Box>
                                <Typography sx={connectorLabelSx}>
                                    <FormattedMessage
                                        id="connectorNumber"
                                        values={{number: connector.connectorId}}
                                    />
                                </Typography>
                                {hasSpec && description && (
                                    <>
                                        <Typography component="span" sx={dotSx}>
                                            <FormattedMessage id="dot"/>
                                        </Typography>
                                        <Typography sx={connectorTypeSx}>
                                            {description.connectorType}
                                        </Typography>
                                    </>
                                )}
                            </>
                        )}
                        {/* The tab already carries the number, the type and the state, so what is
                            left to say here is how much the socket can deliver. */}
                        {hasSpec && description && (
                            <>
                                {standalone && (
                                    <Typography component="span" sx={dotSx}>
                                        <FormattedMessage id="dot"/>
                                    </Typography>
                                )}
                                <Typography sx={connectorPowerSx}>
                                    {description.maxPower}
                                    <FormattedMessage id="kw"/>
                                </Typography>
                            </>
                        )}
                        {standalone && (
                            <Box sx={{ml: 'auto'}}>
                                <ColoredStatus label={status} fontSize={11}/>
                            </Box>
                        )}
                    </Box>
                )}

                {/* An amperage of zero and a duration of 00h:00m are not readings, they are the
                    absence of one, so the figures appear with the session and not before it. */}
                {isCharging && (
                    <Box sx={figuresRowSx}>
                        <ProgressActiveChargingIcon percent={wattsConsumed ? wattsConsumed / 1000 : 0}/>
                        <Box>
                            <Typography sx={deliveredLabelSx}>
                                <FormattedMessage id="delivered"/>
                            </Typography>
                            <Typography sx={deliveredValueSx}>
                                {(wattsConsumed / 1000).toFixed(2)}
                                {' '}
                                <Box component="span" sx={unitSx}>
                                    <FormattedMessage id="kwh"/>
                                </Box>
                            </Typography>
                        </Box>
                        <Box sx={sideFiguresSx}>
                            <Typography sx={deliveredLabelSx}>
                                <FormattedMessage id="amperage"/>
                            </Typography>
                            <Typography sx={sideValueSx}>
                                {amperage ? (+amperage).toFixed(2) : intl.formatMessage({id: 'waitingFoData'})}
                                {amperage && <FormattedMessage id="a"/>}
                            </Typography>
                            <Typography sx={{...deliveredLabelSx as object, mt: 1.25}}>
                                <FormattedMessage id="duration"/>
                            </Typography>
                            <Typography sx={sideValueSx}>
                                {chargingTime ? convertTime(chargingTime) : '00h:00m'}
                            </Typography>
                        </Box>
                    </Box>
                )}

                {showMeta && (
                    <Box sx={metaRowSx}>
                        {sessionUserName && <Typography sx={metaNameSx}>{sessionUserName}</Typography>}
                        {Boolean(sessionUserName && sessionTag) && (
                            <Typography component="span" sx={dotSx}>
                                <FormattedMessage id="dot"/>
                            </Typography>
                        )}
                        {sessionTag && <Typography sx={metaTagSx}>{sessionTag}</Typography>}
                        {cost && (
                            <Typography sx={metaCostSx}>
                                {formatPrice(cost.totalCost)}
                                {' '}
                                {cost.currency}
                            </Typography>
                        )}
                    </Box>
                )}

                {/* An idle socket is a state, not the absence of one: a panel that drew nothing left
                    the reader wondering whether it had failed to load. */}
                {!sessionRunning && (
                    <Typography sx={emptySx}>
                        <FormattedMessage id="noActiveSession"/>
                    </Typography>
                )}

                {showTagField && (
                    <Box sx={{...tagFieldSx as object, mt: 1.75}}>
                        <Box component="button" type="button" sx={tagPickSx} onClick={handleOpenTagModal}>
                            <Typography component="span" sx={tagLabelSx}>
                                <FormattedMessage id="rfidLabel"/>:
                            </Typography>
                            <Typography component="span" sx={tagValueSx}>
                                {selectedTag || intl.formatMessage({id: 'clickToSelectTag'})}
                            </Typography>
                            <Edit sx={{fontSize: 18, color: 'grey.500'}}/>
                        </Box>
                        {selectedTag && (
                            <IconButton
                                size="small"
                                aria-label={intl.formatMessage({id: 'clear'})}
                                onClick={handleClearTag}
                            >
                                <Close sx={{fontSize: 18, color: 'grey.500'}}/>
                            </IconButton>
                        )}
                    </Box>
                )}

                {canManage && (
                    <>
                        <Button
                            variant="contained"
                            sx={sectionButtonSx(isCharging)}
                            disabled={startDisabled}
                            onClick={handleClick}
                        >
                            <FormattedMessage id={isCharging ? 'stop' : 'start'}/>
                        </Button>
                        {/* Why it cannot be pressed, said here rather than left to a request that
                            comes back refused. */}
                        {!canControlSession && (
                            <Typography sx={hintSx}>
                                <FormattedMessage id="sessionNotYours"/>
                            </Typography>
                        )}
                        {canControlSession && isPreparing && !selectedTag && (
                            <Typography sx={hintSx}>
                                <FormattedMessage id="selectCardToStart"/>
                            </Typography>
                        )}
                    </>
                )}
            </Box>

            <SelectOcppTagModal
                open={isTagModalOpen}
                handleClose={handleCloseTagModal}
                onSelectTag={handleSelectTag}
                businessId={charger.business.id}
                companyId={companyId}
            />
        </>
    );
};
